import { defineStore } from "pinia";
import { useI18n } from "vue-i18n";
import { ref } from "vue";
import { ToastMessageOptions } from "primevue";

interface Toast extends ToastMessageOptions {}

const DEFAULT_LIFETIME = 4500;

interface ToastArgs {
  summary?: string;
  detail?: string;
  params?: Record<string, unknown>;
}

export const useToastStore = defineStore("toast", () => {
  const { t } = useI18n();
  const toast = ref<Toast | null>(null);

  function createGenericToast(severity: ToastMessageOptions["severity"]) {
    if (!severity) {
      return {
        summary: t("generic_toast.success.summary"),
        detail: t("generic_toast.success.detail"),
      };
    }
    return {
      summary: t(`generic_toast.${severity}.summary`),
      detail: t(`generic_toast.${severity}.detail`),
    };
  }

  function success({ summary, detail, params = {} }: ToastArgs) {
    if (!summary && !detail) {
      toast.value = {
        severity: "success",
        life: DEFAULT_LIFETIME,
        ...createGenericToast("success"),
      };
      return;
    }
    toast.value = {
      severity: "success",
      summary: summary ? t(summary, params) : undefined,
      detail: detail ? t(detail, params) : undefined,
      life: DEFAULT_LIFETIME,
    };
  }

  function error({ summary, detail, params = {} }: ToastArgs) {
    if (!summary && !detail) {
      toast.value = {
        severity: "error",
        life: DEFAULT_LIFETIME,
        ...createGenericToast("error"),
      };
      return;
    }
    toast.value = {
      severity: "error",
      summary: summary ? t(summary, params) : t("error.generic.summary"),
      detail: detail ? t(detail, params) : t("error.generic.detail"),
      life: DEFAULT_LIFETIME,
    };
  }

  function info({ summary, detail, params = {} }: ToastArgs) {
    if (!summary && !detail) {
      toast.value = {
        severity: "info",
        life: DEFAULT_LIFETIME,
        ...createGenericToast("info"),
      };
      return;
    }
    toast.value = {
      severity: "info",
      summary: summary ? t(summary, params) : undefined,
      detail: detail ? t(detail, params) : undefined,
      life: DEFAULT_LIFETIME,
    };
  }

  function warn({ summary, detail, params = {} }: ToastArgs) {
    if (!summary && !detail) {
      toast.value = {
        severity: "warn",
        life: DEFAULT_LIFETIME,
        ...createGenericToast("warn"),
      };
      return;
    }
    toast.value = {
      severity: "warn",
      summary: summary ? t(summary, params) : undefined,
      detail: detail ? t(detail, params) : undefined,
      life: DEFAULT_LIFETIME,
    };
  }

  return {
    toast,
    success,
    error,
    info,
    warn,
  };
});
